import { PricingTypeEnum } from '@libs/schemas/src/pricing-rule';
import { OrderStatusEnum } from '@libs/schemas/src/order';
import { BundlePricingCard } from 'components/orders/Details/BundlePricingCard';
import { FixedPricingCard } from 'components/orders/Details/FixedPricingCard';
import { PayAsYouGoPricingCard } from 'components/orders/Details/PayAsYouGoPricingCard';
import { AdditionalFeesCard } from 'components/orders/Details/AdditionalFeesCard';
import { OrderTotalSummary } from 'components/orders/Details/OrderTotalSummary';
import { LocationSurcharge } from 'src/components/LocationSurcharge';
import { ChargeSummary } from 'src/components/ChargeSummary';
import { Order, Cart, Material, OrderContainer } from 'src/trpc';
import { extractChargeSummaryPropsFromFinalizedOrder } from '@libs/frontend-services';
import { useOrderAddOnProvenance } from 'components/orders/Details/orderAddOnProvenance';
import { useMemo } from 'react';
import { ContainerBreakdown } from '@libs/schemas';
import { useOrderCurrentPricing } from 'components/orders/Details/useOrderCurrentPricing';

// Address type - only needs city property for this component
interface Address {
  city?: string;
}

interface OrderPricingSectionProps {
  order: Order;
  cart: Cart;
  address?: Address | null;
  allMaterials?: Material[];
  setTotalBalanceInCents?: (value: number | ((prev: number) => number)) => void;
  containers?: OrderContainer[];
  containerBreakdown?: ContainerBreakdown[];
}

export const OrderPricingSection = ({
  order,
  cart,
  address,
  allMaterials,
  setTotalBalanceInCents,
  containers,
  containerBreakdown,
}: OrderPricingSectionProps) => {
  const pricingModel = order.quotedPricingDetails?.pricingModel;
  const quote = order.quotedPricingDetails?.quote;
  const lineItems = cart.lineItems;

  // Determine which pricing card to show
  const showBundlePricing = pricingModel === PricingTypeEnum.BUNDLE && quote;
  const showFixedPricing = pricingModel === PricingTypeEnum.FIXED && quote;
  const showPayAsYouGoPricing =
    (pricingModel === PricingTypeEnum.PAY_AS_YOU_GO || !pricingModel) && lineItems;

  // Determine which sections to show based on order status
  const showTotalSummary =
    ![OrderStatusEnum.Completed, OrderStatusEnum.Refunded, OrderStatusEnum.Restricted].includes(
      order.status as OrderStatusEnum,
    ) && lineItems;

  const showChargeSummary = [OrderStatusEnum.Completed, OrderStatusEnum.Refunded].includes(
    order.status as OrderStatusEnum,
  );

  const showRestrictedMessage = order.status === OrderStatusEnum.Restricted;

  // The cart's line items are an immutable checkout snapshot, so items added to or removed
  // from the live order only show up on `order.addOns`. Reconciling the two is what lets this
  // section list, and price, the add-ons and products the order actually has right now.
  const { retainedFromBooking, addedAfterBooking } = useOrderAddOnProvenance(order, cart);
  const currentPricing = useOrderCurrentPricing(order, cart);

  // Extract ChargeSummary props from finalizedLineItems for completed orders
  const chargeSummaryProps = useMemo(() => {
    if (!showChargeSummary || !containers) {
      return null;
    }
    return extractChargeSummaryPropsFromFinalizedOrder(order, cart, containers, containerBreakdown);
  }, [showChargeSummary, order, cart, containers, containerBreakdown]);

  return (
    <>
      {/* Bundle Pricing Card */}
      {showBundlePricing && quote.pricing.containers[0] && (
        <BundlePricingCard
          containerPricing={quote.pricing.containers[0]}
          dropOffDate={order.dropOffWindow?.day}
          pickupDate={order.pickupWindow?.day}
        />
      )}

      {/* Fixed Pricing Card */}
      {showFixedPricing && quote.pricing.containers[0] && (
        <FixedPricingCard
          containerPricing={quote.pricing.containers[0]}
          dropOffDate={order.dropOffWindow?.day}
          pickupDate={order.pickupWindow?.day}
          selectedMaterials={cart.materials}
          allMaterials={allMaterials}
        />
      )}

      {/* Pay-As-You-Go Pricing Card */}
      {showPayAsYouGoPricing && (
        <PayAsYouGoPricingCard
          quote={quote}
          lineItems={lineItems}
          dropOffDate={order.dropOffWindow?.day}
          pickupDate={order.pickupWindow?.day}
        />
      )}

      {/* Additional Fees Card - renders itself away when there is nothing to price, so it no
          longer needs a lineItems guard: post-booking add-ons must still be listed on an
          order whose cart carries no booking-time line items. It is fed only the booked items
          the order still has, at the prices the order is charged for them, so its rows add up
          to the Subtotal below rather than to the frozen booking-time figures. */}
      <AdditionalFeesCard
        addOns={retainedFromBooking}
        newAddOns={addedAfterBooking}
        cityFeeInCents={lineItems?.cityFeeInCents}
        cityName={address?.city}
      />

      {/* Location Surcharge */}
      {cart._id && <LocationSurcharge cartId={cart._id} />}

      {/* Order Total Summary */}
      {showTotalSummary && <OrderTotalSummary lineItems={lineItems} pricing={currentPricing} />}

      {/* Charge Summary for Completed/Refunded Orders */}
      {showChargeSummary && chargeSummaryProps && (
        <ChargeSummary {...chargeSummaryProps} setTotalBalanceInCents={setTotalBalanceInCents} />
      )}

      {/* Restricted Order Message */}
      {showRestrictedMessage && (
        <div className="card flex flex-col gap-4 justify-start w-full p-4 mt-2">
          <div className="flex flex-col gap-2 text-center">
            <p className="text-text-primary text-sm font-medium">
              Pricing will be determined after materials review
            </p>
            <p className="label text-xs">Contact customer to confirm materials and provide quote</p>
          </div>
        </div>
      )}
    </>
  );
};
